import { CropMetrics } from "protos/portal/metrics";
import { ExpandTypesButton } from "./ExpandTypesButton";
import {
  getMetricName,
  METRIC_AVERAGE_CROP_SIZE_METER,
  METRIC_CROP_DENSITY_SQ_METER,
  METRIC_KEPT_CROPS,
  METRIC_MISSED_CROPS,
  METRIC_SKIPPED_CROPS,
  METRIC_THINNED_CROPS,
  METRIC_VALID_CROPS,
} from "portal/utils/certifiedMetrics";
import { Measurement } from "../measurement/Measurement";
import { MetricHelp } from "portal/components/MetricHelp";
import { MetricNumber } from "portal/utils/metrics";
import { MetricRow, MetricSubsection } from "./MetricSubsection";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { FunctionComponent, useState } from "react";

// Whether thinning was performed on this crop: any thinning conclusion
// (shot, kept, missed, or ignored) was recorded for it.
const wasThinned = (crop: CropMetrics): boolean =>
  crop.thinned !== 0 ||
  crop.kept !== 0 ||
  crop.missed !== 0 ||
  crop.ignored !== 0;

// Crop subsections with thinning metrics are much taller, so fewer fit
// before the list is truncated behind the "more types" button.
const MAX_COLLAPSED_CROP_TYPES = 3;
const MAX_COLLAPSED_THINNED_CROP_TYPES = 2;

export const JigsawCropMetrics: FunctionComponent<{
  cropMetricsByCategory: { [id: string]: CropMetrics };
}> = ({ cropMetricsByCategory }) => {
  const [isExpanded, setExpanded] = useState<boolean>(false);

  const entries = Object.entries(cropMetricsByCategory).sort(
    ([, a], [, b]) => b.found - a.found
  );
  const maxCollapsed = entries
    .slice(0, MAX_COLLAPSED_CROP_TYPES)
    .some(([, crop]) => wasThinned(crop))
    ? MAX_COLLAPSED_THINNED_CROP_TYPES
    : MAX_COLLAPSED_CROP_TYPES;
  const visible = isExpanded ? entries : entries.slice(0, maxCollapsed);

  return (
    <>
      {visible.map(([id, crop]) => (
        <CropCategoryMetrics key={id} crop={crop} />
      ))}
      <ExpandTypesButton
        hiddenCount={entries.length - maxCollapsed}
        isExpanded={isExpanded}
        setExpanded={setExpanded}
      />
    </>
  );
};

// Like InlineMetric, but takes a precomputed per-category value instead of
// looking it up on the response by metric id. The label is de-emphasized
// beneath the strong crop-name header of the enclosing subsection.
const CropMetricRow: FunctionComponent<{
  metric: MetricNumber;
  value: number | undefined;
}> = ({ metric, value }) => {
  const { t } = useTranslation();

  return (
    <MetricRow className="text-xs font-normal">
      <span>
        {getMetricName(t, metric)}
        <MetricHelp metric={metric} className="ms-1 align-middle" />
      </span>
      <Measurement
        cycleSlot={metric.cycleSlot}
        unit={metric.units}
        value={value}
      />
    </MetricRow>
  );
};

const CropCategoryMetrics: FunctionComponent<{
  crop: CropMetrics;
}> = ({ crop }) => {
  const { t } = useTranslation();

  return (
    <MetricSubsection>
      <Typography
        color="textPrimary"
        className="text-[14px] leading-4 pb-1 font-medium"
      >
        {crop.localizedName}
      </Typography>
      <CropMetricRow
        metric={METRIC_CROP_DENSITY_SQ_METER}
        value={crop.densityPerSquareMeter}
      />
      <CropMetricRow
        metric={METRIC_AVERAGE_CROP_SIZE_METER}
        value={crop.averageRadiusMeters}
      />
      <CropMetricRow metric={METRIC_VALID_CROPS} value={crop.found} />
      {wasThinned(crop) && (
        <>
          <Typography
            color="textSecondary"
            className="uppercase mt-2 text-xs font-semibold leading-none"
          >
            {t("views.metrics.sections.thinned")}
          </Typography>
          <CropMetricRow metric={METRIC_THINNED_CROPS} value={crop.thinned} />
          <CropMetricRow metric={METRIC_SKIPPED_CROPS} value={crop.ignored} />
          <CropMetricRow metric={METRIC_MISSED_CROPS} value={crop.missed} />
          <CropMetricRow metric={METRIC_KEPT_CROPS} value={crop.kept} />
        </>
      )}
    </MetricSubsection>
  );
};
